<script>
import { modalState, hideModal } from "@app/store/modalStore.svelte.js";
import { externalDataState } from "@app/store/externalDataStore.svelte.js";
import { toast } from "@zerodevx/svelte-toast";
import { fade } from "svelte/transition";
import { onMount } from "svelte";
import * as m from "@app/paraglide/messages.js";
import { functions } from "@app/configs/firebase.js";
import { httpsCallable } from "firebase/functions";

let loadingViews = $state(false);
let loadingMetadata = $state(false);
let saving = $state(false);
let errorMsg = $state("");
let views = $state([]);
let selectedViewId = $state("");
let viewFields = $state([]);
/** @type {Record<string, string | string[]>} */
let fieldValues = $state({});
/** @type {Record<string, string>} */
let chipDrafts = $state({});

onMount(() => {
    loadViews();
});

function normalizeFieldType(field) {
    let type = String(field?.field_type || "")
        .toLowerCase()
        .replace(/[-\s]/g, "_");
    if (type === "int") return "integer";
    if (type === "bool" || type === "yes_no" || type === "yesno") return "boolean";
    if (type === "datetime") return "date_time";
    if (type === "textarea") return "text_area";
    if (type === "dropdown" || type === "select") return "drop_down";
    if (type === "tag") return "tag_cloud";
    if (type === "text") return "string";
    if (type === "double" || type === "number") return "float";
    return type || "string";
}

function usesChips(field) {
    let type = normalizeFieldType(field);
    return type === "tag_cloud" || (type === "url" && !!field.multi);
}

function isMultiSelect(field) {
    return normalizeFieldType(field) === "drop_down" && !!field.multi;
}

function optionValue(opt) {
    if (opt && typeof opt === "object") return String(opt.value ?? opt.label ?? "");
    return String(opt ?? "");
}

function optionLabel(opt) {
    if (opt && typeof opt === "object") return String(opt.label ?? opt.value ?? "");
    return String(opt ?? "");
}

function extractRawValues(metadataValues, fieldName) {
    let entry = metadataValues?.[fieldName];
    if (!entry) return [];
    let values = entry.field_values || entry;
    if (Array.isArray(values)) {
        return values
            .map((v) => (typeof v === "object" ? v.value : v))
            .filter((v) => v !== undefined && v !== null && v !== "")
            .map((v) => String(v));
    }
    if (typeof values === "object" && values.value !== undefined && values.value !== null && values.value !== "") {
        return [String(values.value)];
    }
    let asString = String(values ?? "");
    return asString ? [asString] : [];
}

function toDateInputValue(value) {
    if (!value) return "";
    let s = String(value);
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    return s.slice(0, 10);
}

function toDateTimeLocalValue(value) {
    if (!value) return "";
    let date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        let s = String(value);
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) return s.slice(0, 16);
        return "";
    }
    let pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function fromDateTimeLocalValue(value) {
    if (!value) return "";
    let date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toISOString().replace(/\.\d{3}Z$/, "Z");
}

function toBooleanInputValue(value) {
    let s = String(value).toLowerCase();
    if (s === "true" || s === "yes" || s === "1") return "true";
    if (s === "false" || s === "no" || s === "0") return "false";
    return "";
}

function extractFieldValue(metadataValues, field) {
    let type = normalizeFieldType(field);
    let raw = extractRawValues(metadataValues, field.name);
    if (usesChips(field) || isMultiSelect(field)) {
        return raw;
    }
    let first = raw[0] ?? "";
    if (type === "date") return toDateInputValue(first);
    if (type === "date_time") return toDateTimeLocalValue(first);
    if (type === "boolean") return toBooleanInputValue(first);
    return first;
}

function isFieldEmpty(value) {
    if (Array.isArray(value)) return value.filter((v) => String(v).trim() !== "").length === 0;
    if (value === undefined || value === null) return true;
    return String(value).trim() === "";
}

function ensureChipArray(fieldName) {
    if (!Array.isArray(fieldValues[fieldName])) {
        fieldValues[fieldName] = [];
    }
    return fieldValues[fieldName];
}

function commitChip(field) {
    let draft = String(chipDrafts[field.name] ?? "").trim();
    if (!draft) return;
    let chips = ensureChipArray(field.name);
    if (!chips.includes(draft)) {
        chips.push(draft);
    }
    chipDrafts[field.name] = "";
}

function removeChip(field, chip) {
    let chips = ensureChipArray(field.name);
    fieldValues[field.name] = chips.filter((c) => c !== chip);
}

function onChipKeydown(event, field) {
    if (event.key === "Enter" || event.key === ",") {
        event.preventDefault();
        commitChip(field);
    } else if (
        event.key === "Backspace" &&
        !String(chipDrafts[field.name] ?? "") &&
        Array.isArray(fieldValues[field.name]) &&
        fieldValues[field.name].length
    ) {
        fieldValues[field.name] = fieldValues[field.name].slice(0, -1);
    }
}

async function loadViews() {
    loadingViews = true;
    errorMsg = "";
    try {
        let res = await httpsCallable(functions, "v8IconikListMetadataViews")({
            category: "default"
        });
        if (res.data?.result === "failed") {
            throw new Error(res.data.message || "Failed to list views");
        }
        views = res.data?.views || [];
        if (views.length > 0) {
            selectedViewId = views[0].id;
            await loadMetadata(selectedViewId);
        }
    } catch (err) {
        console.log(err);
        errorMsg = m.iconik_metadata_views_error({ error: err.message });
    } finally {
        loadingViews = false;
    }
}

async function onViewChange() {
    if (selectedViewId) {
        await loadMetadata(selectedViewId);
    }
}

async function loadMetadata(viewId) {
    loadingMetadata = true;
    errorMsg = "";
    viewFields = [];
    fieldValues = {};
    chipDrafts = {};
    try {
        let res = await httpsCallable(functions, "v8IconikGetAssetMetadata")({
            assetId: externalDataState.assetId,
            viewId
        });
        if (res.data?.result === "failed") {
            throw new Error(res.data.message || "Failed to load metadata");
        }

        let fields = res.data?.viewFields || [];
        let metadataValues = res.data?.metadata_values || {};

        if (fields.length === 0 && metadataValues && typeof metadataValues === "object") {
            fields = Object.keys(metadataValues).map((name) => ({
                name,
                label: name,
                field_type: "string",
                multi: false,
                read_only: false,
                required: false
            }));
        }

        viewFields = fields;
        let nextValues = {};
        let nextDrafts = {};
        fields.forEach((field) => {
            nextValues[field.name] = extractFieldValue(metadataValues, field);
            if (usesChips(field)) {
                nextDrafts[field.name] = "";
            }
        });
        fieldValues = nextValues;
        chipDrafts = nextDrafts;
    } catch (err) {
        console.log(err);
        errorMsg = m.iconik_metadata_load_error({ error: err.message });
    } finally {
        loadingMetadata = false;
    }
}

function buildMetadataValues() {
    /** @type {Record<string, { field_values: { value: string }[] }>} */
    let metadata_values = {};
    viewFields.forEach((field) => {
        if (field.read_only) return;
        let type = normalizeFieldType(field);
        let raw = fieldValues[field.name];
        let values;
        if (usesChips(field) || isMultiSelect(field)) {
            let arr = Array.isArray(raw)
                ? raw
                : String(raw ?? "")
                      .split(",")
                      .map((v) => v.trim())
                      .filter(Boolean);
            values = arr.filter((v) => String(v).trim() !== "").map((value) => ({ value: String(value) }));
        } else if (raw === undefined || raw === null || String(raw).trim() === "") {
            values = [];
        } else if (type === "boolean") {
            values = [{ value: String(raw) === "true" ? "true" : "false" }];
        } else if (type === "date_time") {
            values = [{ value: fromDateTimeLocalValue(String(raw)) }];
        } else {
            values = [{ value: String(raw) }];
        }
        metadata_values[field.name] = { field_values: values };
    });
    return metadata_values;
}

function missingRequiredFields() {
    return viewFields.filter((field) => field.required && !field.read_only && isFieldEmpty(fieldValues[field.name]));
}

async function saveMetadata() {
    saving = true;
    errorMsg = "";
    try {
        if (missingRequiredFields().length > 0) {
            errorMsg = m.iconik_metadata_required_error();
            return;
        }
        let res = await httpsCallable(functions, "v8IconikUpdateAssetMetadata")({
            assetId: externalDataState.assetId,
            viewId: selectedViewId,
            metadata_values: buildMetadataValues()
        });
        if (res.data?.result === "failed") {
            throw new Error(res.data.message || "Update failed");
        }
        toast.push(m.iconik_metadata_save_success_toast(), { classes: ["toast-success"] });
        await loadMetadata(selectedViewId);
    } catch (err) {
        console.log(err);
        toast.push(m.iconik_metadata_save_failed_toast({ error: err.message }), { classes: ["toast-danger"] });
        errorMsg = m.iconik_metadata_save_failed_toast({ error: err.message });
    } finally {
        saving = false;
    }
}

function handleSave(event) {
    event.preventDefault();
    saveMetadata();
}
</script>

<div
    transition:fade={{ duration: 100 }}
    class="modal {modalState.selected === 'iconikMetadata' ? 'show d-block' : ''}"
    role="dialog"
    aria-labelledby="iconikMetadataTitle"
    aria-describedby="iconikMetadataDescription"
    tabindex="-1"
    id="IconikMetadataModal"
>
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
        <form class="modal-content" onsubmit={handleSave}>
            <div class="modal-header">
                <h4 class="modal-title" id="iconikMetadataTitle">
                    <img width="23px" src="./assets/img/logos/iconik-icon.ico" alt={m.iconik_metadata_icon_alt()} />
                    {m.iconik_metadata_title()}
                </h4>
                <button
                    type="button"
                    class="btn-close"
                    id="closeIconikMetadataModal"
                    aria-label={m.iconik_metadata_close_label()}
                    onclick={hideModal}
                ></button>
            </div>
            <div class="modal-body" id="iconikMetadataDescription">
                {#if loadingViews}
                    <p class="text-muted">{m.iconik_metadata_loading()}</p>
                {:else if views.length === 0}
                    <p class="text-muted">{errorMsg || m.iconik_metadata_no_views()}</p>
                {:else}
                    <div class="mb-3">
                        <label class="form-label" for="iconikMetadataViewSelect">{m.iconik_metadata_view_label()}</label>
                        <select
                            class="form-select"
                            id="iconikMetadataViewSelect"
                            disabled={saving || loadingMetadata}
                            bind:value={selectedViewId}
                            onchange={onViewChange}
                        >
                            {#each views as view (view.id)}
                                <option value={view.id}>{view.name}</option>
                            {/each}
                        </select>
                    </div>

                    {#if errorMsg}
                        <p class="text-danger small">{errorMsg}</p>
                    {/if}

                    {#if loadingMetadata}
                        <p class="text-muted">{m.iconik_metadata_loading()}</p>
                    {:else if viewFields.length === 0}
                        <p class="text-muted">{m.iconik_metadata_no_fields()}</p>
                    {:else}
                        <div class="row">
                            {#each viewFields as field (field.name)}
                                {@const type = normalizeFieldType(field)}
                                {@const disabled = saving || field.read_only}
                                {@const required = !!(field.required && !field.read_only)}
                                <div class="col-12 mb-3">
                                    <label class="form-label" for={`meta-field-${field.name}`}>
                                        {field.label || field.name}
                                        {#if field.required}
                                            <span class="text-danger" aria-label={m.iconik_metadata_required()}>*</span>
                                        {/if}
                                        {#if field.read_only}
                                            <span class="badge text-bg-secondary ms-1">{m.iconik_metadata_read_only()}</span>
                                        {/if}
                                    </label>
                                    {#if usesChips(field)}
                                        <div class="form-control chip-input" class:bg-body-secondary={disabled}>
                                            {#each Array.isArray(fieldValues[field.name]) ? fieldValues[field.name] : [] as chip (chip)}
                                                <span class="badge text-bg-secondary d-inline-flex align-items-center">
                                                    {chip}
                                                    {#if !disabled}
                                                        <button
                                                            type="button"
                                                            class="btn btn-sm p-0 ms-1 lh-1 text-white"
                                                            aria-label={m.iconik_metadata_tag_remove({ tag: chip })}
                                                            onclick={() => removeChip(field, chip)}
                                                        >
                                                            <i class="bi bi-x"></i>
                                                        </button>
                                                    {/if}
                                                </span>
                                            {/each}
                                            {#if !field.read_only}
                                                <input
                                                    id={`meta-field-${field.name}`}
                                                    class="chip-draft"
                                                    type={type === "url" ? "url" : "text"}
                                                    disabled={saving}
                                                    required={required && isFieldEmpty(fieldValues[field.name])}
                                                    bind:value={chipDrafts[field.name]}
                                                    onkeydown={(event) => onChipKeydown(event, field)}
                                                    onblur={() => commitChip(field)}
                                                    placeholder={m.iconik_metadata_tag_add_placeholder()}
                                                />
                                            {/if}
                                        </div>
                                    {:else if type === "integer" || type === "float"}
                                        <input
                                            type="number"
                                            class="form-control"
                                            id={`meta-field-${field.name}`}
                                            step={type === "integer" ? "1" : "any"}
                                            min={field.min_value != null ? field.min_value : undefined}
                                            max={field.max_value != null ? field.max_value : undefined}
                                            disabled={disabled}
                                            {required}
                                            bind:value={fieldValues[field.name]}
                                        />
                                    {:else if type === "boolean"}
                                        <select
                                            class="form-select"
                                            id={`meta-field-${field.name}`}
                                            disabled={disabled}
                                            {required}
                                            bind:value={fieldValues[field.name]}
                                        >
                                            <option value=""></option>
                                            <option value="true">{m.iconik_metadata_yes()}</option>
                                            <option value="false">{m.iconik_metadata_no()}</option>
                                        </select>
                                    {:else if type === "date"}
                                        <input
                                            type="date"
                                            class="form-control"
                                            id={`meta-field-${field.name}`}
                                            disabled={disabled}
                                            {required}
                                            bind:value={fieldValues[field.name]}
                                        />
                                    {:else if type === "date_time"}
                                        <input
                                            type="datetime-local"
                                            class="form-control"
                                            id={`meta-field-${field.name}`}
                                            disabled={disabled}
                                            {required}
                                            bind:value={fieldValues[field.name]}
                                        />
                                    {:else if type === "drop_down" && field.multi}
                                        <select
                                            class="form-select"
                                            id={`meta-field-${field.name}`}
                                            disabled={disabled}
                                            {required}
                                            multiple
                                            bind:value={fieldValues[field.name]}
                                        >
                                            {#each field.options || [] as opt (optionValue(opt))}
                                                <option value={optionValue(opt)}>{optionLabel(opt)}</option>
                                            {/each}
                                        </select>
                                    {:else if type === "drop_down"}
                                        <select
                                            class="form-select"
                                            id={`meta-field-${field.name}`}
                                            disabled={disabled}
                                            {required}
                                            bind:value={fieldValues[field.name]}
                                        >
                                            <option value=""></option>
                                            {#each field.options || [] as opt (optionValue(opt))}
                                                <option value={optionValue(opt)}>{optionLabel(opt)}</option>
                                            {/each}
                                        </select>
                                    {:else if type === "email"}
                                        <input
                                            type="email"
                                            class="form-control"
                                            id={`meta-field-${field.name}`}
                                            disabled={disabled}
                                            {required}
                                            bind:value={fieldValues[field.name]}
                                        />
                                    {:else if type === "url"}
                                        <input
                                            type="url"
                                            class="form-control"
                                            id={`meta-field-${field.name}`}
                                            disabled={disabled}
                                            {required}
                                            bind:value={fieldValues[field.name]}
                                        />
                                    {:else if type === "text_area"}
                                        <textarea
                                            class="form-control"
                                            id={`meta-field-${field.name}`}
                                            rows="3"
                                            disabled={disabled}
                                            {required}
                                            bind:value={fieldValues[field.name]}
                                        ></textarea>
                                    {:else if type === "string"}
                                        <input
                                            type="text"
                                            class="form-control"
                                            id={`meta-field-${field.name}`}
                                            disabled={disabled}
                                            {required}
                                            bind:value={fieldValues[field.name]}
                                        />
                                    {:else}
                                        <textarea
                                            class="form-control"
                                            id={`meta-field-${field.name}`}
                                            rows="2"
                                            disabled={disabled}
                                            {required}
                                            bind:value={fieldValues[field.name]}
                                        ></textarea>
                                    {/if}
                                </div>
                            {/each}
                        </div>
                    {/if}
                {/if}
            </div>
            <div class="modal-footer">
                <button
                    type="submit"
                    class="btn btn-primary"
                    disabled={saving || loadingViews || loadingMetadata || !selectedViewId || viewFields.length === 0}
                >
                    {#if saving}
                        {m.iconik_metadata_saving()}
                    {:else}
                        {m.iconik_metadata_save_button()}
                    {/if}
                </button>
                <button type="button" class="btn btn-light" disabled={saving} onclick={hideModal}>
                    {m.iconik_metadata_cancel_button()}
                </button>
            </div>
        </form>
    </div>
</div>

<style>
.chip-input {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    align-items: center;
    min-height: calc(1.5em + 0.75rem + 2px);
}
.chip-draft {
    border: 0;
    outline: 0;
    flex: 1 1 8rem;
    min-width: 8rem;
    background: transparent;
    padding: 0;
}
.chip-input:focus-within {
    border-color: #86b7fe;
    box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}
</style>
